import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { adminBotSocket, spotSocket } from 'src/app/app.module';
import { fraction, longDecimals, nFormatter, toFixedDown, truncateDecimals } from 'src/app/lib/roundOf';
import { adminBotService } from 'src/app/services/bot.admin.service';
import { WalletService } from 'src/app/services/wallet.service';
import { AuthFacade } from 'src/app/store/auth';
import { TradeAssetFacade } from 'src/app/store/bot-trade-asset';
import { PairFacade } from 'src/app/store/botpair';
import { PositionDetailsFacade } from 'src/app/store/positionDetails';
import { ToastrService } from 'ngx-toastr';
import isEmpty from 'src/app/lib/isEmpty';
import { HttpEventType, HttpEvent } from '@angular/common/http';
import { dateHours, dateHoursClose, dateHourswithTime, dateTimeFormat } from 'src/app/lib/dateFilter';

@Component({
  selector: 'app-TopGainer',
  templateUrl: './TopGainer.component.html',
  styleUrls: ['./TopGainer.component.css']
})
export class TopGainerComponent implements OnInit {

  onWheel(event: WheelEvent) {
    event.preventDefault(); // Prevents the default scroll behavior
  }

  // Helpers
  toFixedDown = toFixedDown;
  truncateDecimals = truncateDecimals;
  longDecimals = longDecimals;
  fraction = fraction;
  nFormatter = nFormatter;
  dateTimeFormat = dateTimeFormat
  // Data State
  topGainers: any[] = [];
  recentTrade: any = [];
  auth: any = {};
  pairs: any = [];

  // Selection & UI State
  searchPair: string = '';
  activePair: string = '';
  selectedPair: any = null;
  investmentAmount: any | null = null;
  serverErrors: any = {}
  isDownloading = false;
  downloadProgress = 0
  submitLoader: boolean = false;
  changeSort: 'gainers' | 'losers' | '' = '';

  constructor(
    private pairFacade: PairFacade,
    private router: Router,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private location: Location,
    private service: adminBotService,
    private authFacade: AuthFacade,
    private walletService: WalletService,
    private tradeAssetFacade: TradeAssetFacade,
    private positionDetails: PositionDetailsFacade,
    private spotSocket: spotSocket,
    private botSocket: adminBotSocket
  ) {
    this.authFacade.auth$.subscribe({
      next: (userAuth: any) => {
        this.auth = userAuth;
      },
      error: (err: any) => { }
    });
  }

  ngOnInit() {
    this.getPairs(); // Assuming this exists elsewhere in your code
    this.getRecentGainer();

    // 1. Handle URL parsing safely
    const currentPath = this.location.path();
    if (currentPath) {
      const parts = currentPath.split('/');
      if (parts.length > 2) {
        this.activePair = parts[2];
      }
    }

    // 2. Socket Listeners
    this.botSocket.on('VMAP_Pair', (data: any) => {
      this.processTopGainersUpdate({ type: 'UPDATE_OR_ADD_PAIR', payload: data });
    });

    this.botSocket.on('marketPrice', (data: any) => {
      this.processTopGainersUpdate({ type: 'UPDATE_MARKET_PRICE', payload: data });
    });
  }
  /**
     * CENTRAL GATEKEEPER
     * This is the ONLY function that modifies 'this.topGainers'.
     */
  processTopGainersUpdate(action: { type: string, payload: any }) {
    // Read current list once
    const currentList = this.topGainers || [];
    let newList: any[] = [];

    switch (action.type) {
      // A. Handle Initial API Load (with deduplication)
      case 'SET_INITIAL_LIST':
        newList = this._deduplicateHistory(action.payload);
        break;

      // B. Handle New Pair / VMAP Update (Move to Top)
      case 'UPDATE_OR_ADD_PAIR':
        newList = this._handleVmPairUpdate(currentList, action.payload);

        // Update selectedPair if it matches
        if (this.selectedPair && this.selectedPair.symbol === action.payload.symbol) {
          this.selectedPair = { ...this.selectedPair, ...action.payload };
        }
        break;

      // C. Handle Market Price Update (Update in place)
      case 'UPDATE_MARKET_PRICE':
        newList = this._handleMarketPriceUpdate(currentList, action.payload);
        break;

      default:
        newList = currentList;
    }

    // Apply the new list
    this.topGainers = newList;
  }

  /**
   * API CALL
   * Routes the result through the gatekeeper to ensure thread safety and deduplication.
   */
  getRecentGainer() {
    if (!this.auth.signedIn) {
      return;
    }

    let param = {
      skip: 0,
      limit: 200 // Fetch extra to ensure we have enough after removing duplicates
    };

    this.service.getOpportunityHistory({ filter: param })
      .subscribe({
        next: (v: any) => {
          if (v.success && Array.isArray(v.result)) {
            // Send to gatekeeper instead of direct assignment
            this.processTopGainersUpdate({ type: 'SET_INITIAL_LIST', payload: v.result });
          }
        },
        error: (err: any) => {
          console.error('Error fetching history:', err);
        }
      });
  }

  // ==========================================================
  // HELPER FUNCTIONS (PURE LOGIC)
  // ==========================================================

  /**
   * Logic: 
   * 1. Remove the symbol if it already exists (anywhere in list).
   * 2. Add the new/updated symbol to the VERY TOP (index 0).
   * 3. Limit list to 50 items (optional).
   */
  private _handleVmPairUpdate(currentList: any[], result: any): any[] {
    if (!result || typeof result !== 'object' || Array.isArray(result) || !result.symbol) {
      return currentList;
    }

    // Remove existing instance of this symbol
    const listWithoutSymbol = currentList.filter(item => item.symbol !== result.symbol);

    // Add new result to the front
    const listWithNewItem = [result, ...listWithoutSymbol];

    // Optional: Keep list size manageable (e.g., max 50)
    // return listWithNewItem.slice(0, 50); 
    return listWithNewItem;
  }

  /**
   * Logic: Update price/percentage in place. Do NOT change order.
   */
  private _handleMarketPriceUpdate(currentList: any[], result: any): any[] {
    if (!currentList || currentList.length === 0 || !result?.symbol) {
      return currentList;
    }

    const markPrice = parseFloat(result?.data?.bestBid ?? result?.data?.markPrice);

    return currentList.map(item => {
      // If symbols don't match, return original item
      if (item.symbol !== result.symbol) {
        return item;
      }

      // Calculate percentage (ensure your formula here is exactly what you need)
      let currentPercentage = markPrice / (item.price / item.change);

      // Return updated object
      return {
        ...item,
        markPrice: !isNaN(markPrice) ? markPrice : item.markPrice,
        currentPercentage: !isNaN(currentPercentage) ? currentPercentage : item.currentPercentage
      };
    });
  }

  /**
   * Logic: Iterate through API history. Keep only the FIRST occurrence of a symbol.
   * This assumes the API returns data sorted by Time (Newest -> Oldest).
   */
  private _deduplicateHistory(rawList: any[]): any[] {
    const uniqueMap = new Map();

    for (const item of rawList) {
      // Only add if we haven't seen this symbol yet
      if (item.symbol && !uniqueMap.has(item.symbol)) {
        uniqueMap.set(item.symbol, item);
      }
    }

    return Array.from(uniqueMap.values());
  }

  // FIX 1: TrackBy Function for Angular Performance
  trackBySymbol(index: number, item: any): string {
    return item.symbol;
  }
  handleSearchPair(event: any) {
    this.searchPair = event.target.value.toUpperCase().replace('/', '');
  }

  getPairs() {
    this.pairFacade.pair$.subscribe({
      next: (pairs: any) => {
        this.pairs = pairs;
      },
      error: (err: any) => { }
    });
  }

  downloadExcel(botId: any) {
    this.isDownloading = true;
    this.downloadProgress = 0;

    this.service.downloadOpportunity({ botId }).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.DownloadProgress) {
          // event.loaded and event.total (if server provides Content-Length)
          if (event.total) {
            this.downloadProgress = Math.round(100 * event.loaded / event.total);
          }
        } else if (event.type === HttpEventType.Response) {
          if (event.body) {
            const blob = event.body as Blob;
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);

            a.href = objectUrl;
            a.download = 'Opportunity-History.csv';

            // FIX: Append to body, click, then remove
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(objectUrl);
            this.isDownloading = false;
          }
        }
      },
      error: (err) => {
        console.error(err);
        this.isDownloading = false;
      }
    });
  }
  // FIX 3: Pair Selection Logic
  changePair(pair: any) {
    // Immediate UI update
    this.selectedPair = pair;

    // Reset investment amount when switching pairs
    this.investmentAmount = null;

    const newPair = pair.symbol;

    // Avoid reloading if it's strictly the active page pair (optional check)
    if (newPair === this.activePair) return false;

    if (this.activePair) {
      this.botSocket.emit("unSubscribe", this.activePair.replace("_", ""));
    }

    // Find full pair details if available in 'this.pairs'
    const originalPair = this.pairs.find((v: any) => v.symbol === newPair);

    // Use found pair, or fallback to the clicked socket pair object
    const pairToUse = originalPair ? originalPair : pair;

    // Create a safe copy
    const fullPair = { ...pairToUse };
    this.selectedPair = fullPair;
    // Safe string replacement
    if (fullPair.symbol) {
      fullPair.firstCurrencySymbol = fullPair.symbol.replace("USDT", "");
      fullPair.secondCurrencySymbol = "USDT";
    }

    this.service.setTradePair(fullPair);
    this.positionDetails.setPosition({});

    return false;
  }

  createBot() {
    this.submitLoader = true;

    if (!this.selectedPair) {
      this.toastr.error("Please select a pair first");
      return;
    }
    if (!this.investmentAmount || this.investmentAmount <= 0) {
      this.toastr.error("Please enter a valid investment amount");
      return;
    }

    let pairName = this.selectedPair.symbol
    let genDet = {
      pairName: this.selectedPair.symbol,
      firstCurrencySymbol: this.selectedPair.firstCurrencySymbol,
      secondCurrencySymbol: this.selectedPair.secondCurrencySymbol,
      totalBots: 1,
      investment: parseFloat(this.investmentAmount),
      isManual: true,

    }
    let reqData = {
      pairName: pairName,
      pairId: this?.selectedPair?._id,
      exchange: "binance",
      BotType: "VMAP",
      RunStatus: false,
      tradesetting: genDet,
      orderType: "Limit",
      validate: false
    };
    this.toastr.success("Please wait for a while");
    this.service.orderPlace(reqData).subscribe({
      next: (v: any) => {
        this.toastr.success(v.message)
        reqData.validate = false
        this.submitLoader = false;
        this.investmentAmount = 0
      },
      error: (err: any) => {
        this.submitLoader = false;

        reqData.validate = false

        if ([400, 404].includes(err.status)) {
          if (err?.error?.message)
            this.toastr.error(err?.error?.message);
          const validationErrors = err.error.errors;
          if (!isEmpty(validationErrors)) {
            this.serverErrors = validationErrors || {};
            this.toastr.error(err.error.message)
            if (err?.error?.order) {
              this.toastr.error(err.error.order)
            }
          }
        } else if (err.status === 500) {
          this.toastr.error(err.error.message);
        }
      },
      complete: () => { },
    });
  }
  // bot create
  handleTradeChange(e: any): void {
    e.preventDefault()
    let { name, value } = e.target
    if (
      ["investment", "upperPrice", "lowerPrice", "grids", "gridStepPercent", "gridStepValue", "takeprofitRatio",
        "takeprofitCallback", "stopLoss", "buyPercentage", "buyCount", "sellPercentage", "sellCount"].includes(name)
    ) {
      //number 
      const isValidFloat = /^(\d+)?(\.\d{0,10})?$/.test(value) || value === '' || value === '.';
      if (!isValidFloat) return
    }

    //number 
    // cur decimals based on binance
    if (["upperPrice", "lowerPrice", "gridStepValue", "investment"].includes(name)) value = toFixedDown(value, 2)
    // if (["investment"].includes(name)) value = toFixedDown(value, this.pairData?.secondFloatDigit)

    this.investmentAmount = value;
    if (value) {
      this.serverErrors = { ...this.serverErrors, ...{ [name]: "" } };
    }
  }

  filterFloatKeydown(event: KeyboardEvent, fieldName: string): void {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'
    ];
    // allow navigation, shortcuts, etc.
    if (
      allowedKeys.includes(event.key) ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    // only digits or dot
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    const decimalLimit = 2;

    const input = event.target as HTMLInputElement;
    const { selectionStart: start, selectionEnd: end, value } = input;
    if (start === null || end === null) {
      // fall back to default
      return;
    }

    // build what the new value *would* be
    const before = value.slice(0, start);
    const after = value.slice(end);
    const newValue = before + event.key + after;

    // regex: any number of digits, optionally a dot followed by up to decimalLimit digits
    const re = new RegExp(`^\\d*(?:\\.\\d{0,${decimalLimit}})?$`);

    if (!re.test(newValue)) {
      event.preventDefault();
      return;
    }
  }


  toggleChangeSort() {
    if (this.changeSort === '') {
      this.changeSort = 'gainers';
    } else if (this.changeSort === 'gainers') {
      this.changeSort = 'losers';
    } else {
      this.changeSort = '';
    }
    this.sortPairs();
  }

  sortPairs() {
    // Sorting logic applied to 'this.pairs' (Note: usually this applies to topGainers too if you want the table sorted)
    const pairsCopy = [...this.pairs];
    if (this.changeSort === 'gainers') {
      this.pairs = pairsCopy.sort((a, b) => (b.change || 0) - (a.change || 0));
    } else if (this.changeSort === 'losers') {
      this.pairs = pairsCopy.sort((a, b) => (a.change || 0) - (b.change || 0));
    } else {
      this.pairs = pairsCopy;
    }
  }
}